import { useMutation } from '@tanstack/react-query';
import { toast } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { queryClient } from '../../shared/providers/react-query-provider';
import { useGetLoggedAgent } from '../agent/hooks';

import { setAgentAwayStatus, setAgentWorkload } from './api';
import {
    getAgentAwayStatusQueryKey,
    getAgentWorkloadQueryKey,
    getAgencyCapacityFillingStatusQueryKey,
} from './queries';
import { AgentWorkload } from './types';
import { clearWorkloadNeverConfiguredSince } from './utils';
import { logMutationSuccess, logMutationError } from './logging';

/**
 * Mutation to set the agent away status
 * @returns Mutation hook for setting agent away status
 */
export const useSetAgentAwayStatusMutation = () => {
    const { formatMessage } = useIntl();
    const agent = useGetLoggedAgent();

    return useMutation({
        mutationFn: (isAgentAway: boolean) => setAgentAwayStatus(isAgentAway),
        onSuccess: async (_data, isAgentAway) => {
            // TODO: temporary logging for monitoring
            logMutationSuccess('updated agent away status', { isAgentAway }, agent);

            // Invalidate the away status query to refetch
            await queryClient.invalidateQueries({
                queryKey: getAgentAwayStatusQueryKey(),
            });

            // Show success notification
            toast.success(
                formatMessage({
                    id: isAgentAway
                        ? 'agent_settings.is_away.notifications.set_away_status_success'
                        : 'agent_settings.is_away.notifications.unset_away_status_success',
                }),
            );
        },
        onError: (error, isAgentAway) => {
            logMutationError('update agent away status', error, { isAgentAway }, agent);

            // Show error notification
            toast.error(
                formatMessage({
                    id: 'agent_settings.is_away.notifications.updated_failure',
                }),
            );
        },
    });
};

/**
 * Mutation to set the agent workload status
 * @returns Mutation hook for setting agent workload
 */
export const useSetAgentWorkloadMutation = () => {
    const agent = useGetLoggedAgent();

    return useMutation({
        mutationFn: (workload: NonNullable<AgentWorkload>) => setAgentWorkload(workload),
        onSuccess: async (_data, workload) => {
            // TODO: temporary logging for monitoring
            logMutationSuccess(
                'updated agent workload',
                {
                    workloadStatus: workload.status,
                    overloadedReason: workload.overloadedReason,
                },
                agent,
            );

            // Clear the "never configured" flag from localStorage on success
            clearWorkloadNeverConfiguredSince();

            // Invalidate the workload query to refetch
            await queryClient.invalidateQueries({
                queryKey: getAgentWorkloadQueryKey(),
            });

            // Invalidate capacity status as workload affects capacity reminders
            await queryClient.invalidateQueries({
                queryKey: getAgencyCapacityFillingStatusQueryKey(),
            });
        },
        onError: (error, workload) => {
            logMutationError(
                'update agent workload',
                error,
                {
                    workloadStatus: workload.status,
                    overloadedReason: workload.overloadedReason,
                },
                agent,
            );
            // No user notification - handled silently as per original saga behavior
        },
    });
};
